/**
 * Author-chosen Instagram options that ride on the container: collaborators,
 * location and licensed audio. Stored by the editor in
 * `media_keys.perPlatform.instagram`, forwarded by the dispatcher as the body
 * key `instagramOptions`, and mapped to Graph API fields here.
 *
 * Sources, read 2026-11-03:
 *  - collaborators (up to 3 usernames), location_id:
 *    https://developers.facebook.com/docs/instagram-platform/instagram-graph-api/reference/ig-user/media
 *  - audio_configuration {audio_id, audio_volume, video_volume}:
 *    https://developers.facebook.com/documentation/instagram-platform/content-publishing/audio-api
 *
 * Pure (no DOM, no Deno) so it has a Vitest gate; edge functions import it
 * through `_shared/social-api-clients/instagram-publish-options.ts`.
 */

export const INSTAGRAM_COLLABORATORS_MAX = 3;

export type InstagramAudioKind = 'music' | 'original_sound';

export interface InstagramAudioChoice {
  id: string;
  name: string;
  artist?: string | null;
  kind: InstagramAudioKind;
  /** Whole number 1 to 100 (Meta's range), the volume of the attached audio. */
  audioVolume: number;
  /** Whole number 1 to 100, the volume of the video's own sound. */
  videoVolume: number;
}

export interface InstagramPublishOptions {
  collaborators?: string[];
  location?: { id: string; name?: string | null } | null;
  audio?: InstagramAudioChoice | null;
}

/** What the container carries after cleaning. Every key is optional. */
export interface InstagramContainerExtras {
  collaborators?: string[];
  locationId?: string;
  audioConfiguration?: { audioId: string; audioVolume: number; videoVolume: number };
}

const USERNAME_RE = /^[a-z0-9._]{1,30}$/;

/** One username, lower case, no leading @; null when it is not a valid Instagram handle. */
export function normalizeInstagramUsername(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const name = value.trim().replace(/^@+/, '').toLowerCase();
  return USERNAME_RE.test(name) ? name : null;
}

/** Valid, de-duplicated handles, at most three (Meta's limit). */
export function cleanInstagramCollaborators(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  for (const entry of value) {
    const name = normalizeInstagramUsername(entry);
    if (name) seen.add(name);
    if (seen.size === INSTAGRAM_COLLABORATORS_MAX) break;
  }
  return [...seen];
}

/** A Facebook Page id used as the place; digits only. */
export function cleanInstagramLocationId(value: unknown): string | undefined {
  const id =
    typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : '';
  return /^\d{3,25}$/.test(id) ? id : undefined;
}

export const INSTAGRAM_AUDIO_VOLUME = { min: 1, max: 100, default: 100 } as const;

/** Meta takes whole numbers 1 to 100; anything else is rounded and clamped, a non-number becomes 100. */
function clampVolume(value: unknown): number {
  const n = typeof value === 'number' ? value : Number.NaN;
  if (!Number.isFinite(n)) return INSTAGRAM_AUDIO_VOLUME.default;
  return Math.min(INSTAGRAM_AUDIO_VOLUME.max, Math.max(INSTAGRAM_AUDIO_VOLUME.min, Math.round(n)));
}

/** The editor's saved options, as Graph API container extras. Unknown shapes give an empty object. */
export function instagramContainerExtras(options: unknown): InstagramContainerExtras {
  if (!options || typeof options !== 'object') return {};
  const o = options as Record<string, unknown>;
  const extras: InstagramContainerExtras = {};

  const collaborators = cleanInstagramCollaborators(o.collaborators);
  if (collaborators.length > 0) extras.collaborators = collaborators;

  const location =
    o.location && typeof o.location === 'object' ? (o.location as Record<string, unknown>) : null;
  const locationId = cleanInstagramLocationId(location?.id ?? o.locationId);
  if (locationId) extras.locationId = locationId;

  const audio =
    o.audio && typeof o.audio === 'object' ? (o.audio as Record<string, unknown>) : null;
  const audioId = typeof audio?.id === 'string' ? audio.id.trim() : '';
  if (audioId) {
    extras.audioConfiguration = {
      audioId,
      audioVolume: clampVolume(audio?.audioVolume),
      videoVolume: clampVolume(audio?.videoVolume),
    };
  }
  return extras;
}
